import { Link } from "react-router-dom";

const VARIANTS = {
  "btn-primary":
    "inline-flex items-center gap-2 bg-blue-500 hover:bg-blue-600 transition-colors text-white text-sm font-semibold px-6 py-3 rounded-full shadow-lg",
  "btn-outline":
    "inline-flex items-center gap-2 border border-white text-white hover:bg-white hover:text-gray-900 transition-colors text-sm font-semibold px-6 py-3 rounded-full",
  "btn-dark":
    "inline-flex items-center gap-2 bg-gray-900 hover:bg-black transition-colors text-white text-sm font-semibold px-6 py-3 rounded-full shadow-lg",
  "hero-heading":
    "text-3xl md:text-4xl lg:text-6xl font-bold leading-[1.5] drop-shadow-lg max-w-2xl",
  "hero-sub": "text-sm md:text-base text-white/90 drop-shadow",
  "section-heading": "text-2xl md:text-4xl font-bold text-gray-900",
  body: "text-sm text-gray-600 leading-relaxed",
  label: "text-xs font-medium text-gray-500 uppercase tracking-wider",
  "btn-white":
    "inline-block text-nowrap bg-white text-gray-900 text-xs font-semibold px-4 py-2 rounded-sm hover:bg-gray-100 transition-colors",
  "nav-post-title":
    "text-xl font-semibold text-gray-900 group-hover:text-blue-500 transition-colors leading-snug",
  "card-title": "text-gray-900 font-semibold text-sm leading-snug line-clamp-2",
  "card-desc": "text-gray-500 text-xs leading-relaxed line-clamp-3",
  "btn-icon": "text-gray-900 hover:text-blue-500 transition-colors",
  "nav-logo":
    "absolute left-1/2 -translate-x-1/2 lg:static lg:translate-x-0 flex-shrink-0",
  "black-box":
    "block bg-black aspect-square w-[40px] text-white text-center text-xs flex items-center justify-center",
  "nav-link":
    "flex items-center gap-1 text-sm font-medium py-7 transition-colors text-gray-900 hover:text-blue-500",
  "nav-link-active":
    "flex items-center gap-1 text-sm font-medium py-7 transition-colors text-blue-500",
  "nav-link-label": "nav-link-underline",
  "nav-link-label-active": "nav-link-underline nav-link-underline-active",
};

const SEO_DEFAULTS = {
  h1: { role: "heading", "aria-level": 1 },
  h2: { role: "heading", "aria-level": 2 },
  h3: { role: "heading", "aria-level": 3 },
  h4: { role: "heading", "aria-level": 4 },
  h5: { role: "heading", "aria-level": 5 },
  h6: { role: "heading", "aria-level": 6 },
  p: {},
  span: {},
  a: { role: "link" },
  button: { type: "button" },
  label: {},
  strong: {},
  em: {},
  small: {},
  blockquote: { cite: undefined },
};

const Text = ({ is = "p", children, className = "", variant, ...props }) => {
  const variantClass = variant ? (VARIANTS[variant] ?? "") : "";
  const seoAttrs = SEO_DEFAULTS[is] ?? {};

  if (is === "a") {
    const { href, target, rel, ...rest } = props;
    const isExternal =
      href &&
      (href.startsWith("http") || href.startsWith("//") || target === "_blank");
    const anchorAttrs = {
      "aria-label":
        rest["aria-label"] ||
        (typeof children === "string" ? children : undefined),
    };

    if (isExternal) {
      return (
        <a
          href={href}
          target={target}
          rel={target === "_blank" ? "noopener noreferrer" : rel}
          className={`${variantClass} ${className}`.trim()}
          {...anchorAttrs}
          {...rest}
        >
          {children}
        </a>
      );
    }

    return (
      <Link
        to={href ?? "/"}
        className={`${variantClass} ${className}`.trim()}
        {...anchorAttrs}
        {...rest}
      >
        {children}
      </Link>
    );
  }

  const Tag = is;
  return (
    <Tag
      className={`${variantClass} ${className}`.trim()}
      {...seoAttrs}
      {...props}
    >
      {children}
    </Tag>
  );
};

export default Text;
